import type Hls from "hls.js/light";
import type { Backend } from "../api/backend";
import type { Block } from "./blocks";
import type { Output } from "./engine";

/** Ramps a 0…1 volume in small steps. Timers, not animation frames: a background tab
 * gets no frames, and the radio mostly plays in one. */
export async function rampVolume(
  read: () => number,
  write: (volume: number) => void,
  toVolume: number,
  ms: number,
): Promise<void> {
  const from = read();
  const steps = Math.max(1, Math.round(ms / 30));
  for (let step = 1; step <= steps; step += 1) {
    await new Promise((resolve) => setTimeout(resolve, ms / steps));
    write(from + ((toVolume - from) * step) / steps);
  }
}

/**
 * An `<audio>` element as an output. The voice player plays the host's signed MP3s; the
 * SoundCloud output feeds it an HLS stream.
 */
class AudioElementOutput implements Output {
  onEnded: (() => void) | null = null;
  protected readonly audio = new Audio();
  private generation = 0;

  constructor() {
    this.audio.preload = "auto";
    this.audio.addEventListener("ended", () => this.onEnded?.());
  }

  get position(): number {
    return this.audio.currentTime || 0;
  }

  get duration(): number {
    return Number.isFinite(this.audio.duration) ? this.audio.duration : 0;
  }

  async start(block: Block): Promise<void> {
    const generation = ++this.generation;
    const source = await this.source(block);
    if (generation !== this.generation) throw new Error("superseded");
    this.audio.volume = 1;
    await this.attach(source);
    const failed = new Promise<never>((_, reject) => {
      this.audio.addEventListener("error", () => reject(new Error("audio failed to load")), {
        once: true,
      });
    });
    // play() rejects with NotAllowedError when the browser wants a click first.
    await Promise.race([this.audio.play(), failed]);
  }

  pause(): void {
    this.audio.pause();
  }

  resume(): Promise<void> {
    return this.audio.play();
  }

  stop(): void {
    this.generation += 1;
    this.audio.pause();
    this.detach();
  }

  fade(toVolume: number, ms: number): Promise<void> {
    return rampVolume(
      () => this.audio.volume,
      (volume) => {
        this.audio.volume = Math.min(1, Math.max(0, volume));
      },
      toVolume,
      ms,
    );
  }

  /** The URL to play for this block. */
  protected async source(block: Block): Promise<string> {
    if (block.kind !== "voice") throw new Error("not a voice block");
    return block.audioURL;
  }

  protected async attach(source: string): Promise<void> {
    this.audio.src = source;
  }

  protected detach(): void {
    this.audio.removeAttribute("src");
    this.audio.load();
  }
}

export class VoiceOutput extends AudioElementOutput {}

/**
 * SoundCloud tracks: the backend resolves a signed HLS URL (and spends a play from the
 * daily quota). Safari plays HLS natively; other browsers get hls.js, loaded on first use.
 */
export class SoundCloudOutput extends AudioElementOutput {
  private hls: Hls | null = null;

  constructor(private readonly backend: Backend) {
    super();
  }

  protected override async source(block: Block): Promise<string> {
    if (block.kind !== "track") throw new Error("not a track block");
    return (await this.backend.soundCloudStream(block.playableID)).stream_url;
  }

  protected override async attach(source: string): Promise<void> {
    if (this.audio.canPlayType("application/vnd.apple.mpegurl")) {
      this.audio.src = source;
      return;
    }
    const { default: HlsPlayer } = await import("hls.js/light");
    if (!HlsPlayer.isSupported()) throw new Error("HLS is not supported in this browser");
    this.hls = new HlsPlayer();
    this.hls.loadSource(source);
    this.hls.attachMedia(this.audio);
  }

  protected override detach(): void {
    this.hls?.destroy();
    this.hls = null;
    super.detach();
  }
}
